import {
	ControlValueAccessor,
	FormGroup,
	FormBuilder,
	NgControl,
	AbstractControlOptions,
	FormArray,
} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { OnDestroy, AfterViewInit, ViewChild, Directive } from '@angular/core';
import { Subject } from 'rxjs';
import { FormConfig } from './form-cva.component';
import { AbstractControlDataSource } from './abstract-control-data-source';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

/*
	This table component is built using a form array to allow for easier editing.
	The important thing to remember is that the "parent" FormGroup is just a wrapper
	and has no actual value outside of giving us somewhere to store a FormArray. As of
	Angular 7 (and probably 8), there is no way to have a FormArray be the top level
	of a form.
*/

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class CustomFormListAccessor<T extends object>
	implements ControlValueAccessor, AfterViewInit, OnDestroy {
	@ViewChild(MatSort, { static: true }) sort: MatSort;
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

	destroyed = new Subject<void>();

	disabled: boolean;
	// Form Controls
	parent = new FormGroup({
		array: new FormArray([]),
	});

	dataSource = new AbstractControlDataSource<T>(this.array);

	constructor(
		public ngControl: NgControl,
		public fb: FormBuilder,
		public config: FormConfig<T>,
		public options: AbstractControlOptions = {}
	) {
		ngControl.valueAccessor = this;
	}

	ngAfterViewInit() {
		this.dataSource.sort = this.sort;
		this.dataSource.paginator = this.paginator;
	}

	// model --> view
	writeValue(value: T[]): void {
		if (value && value.length > 0) {
			this.array.clear();
			value.forEach((v) => {
				const fc = this.fb.group(this.config) as FormGroup;
				fc.patchValue(v);
				this.array.push(fc);
			});
		} else {
			this.array.reset([]);
		}
	}

	// view --> model
	registerOnChange(fn: (value: T[]) => void) {
		this.array.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};

	setDisabledState?(isDisabled: boolean) {
		this.disabled = isDisabled;
		if (isDisabled) {
			this.array.disable();
		} else {
			this.array.enable();
		}
	}

	get array() {
		return this.parent.get('array') as FormArray;
	}

	ngOnDestroy() {
		this.destroyed.next();
		this.destroyed.complete();
	}
}
